/* Вкладка рисуется классами `rm-*` из стилей раздела «Примеры», а тот чанк
   подгружается только при заходе в пользовательский раздел. Админ, открывший
   вкладку первой, видел её без стилей вовсе — заметнее всего по шеврону: без
   размеров SVG растягивался в огромную «галку». Модуль подключает свои стили
   сам, как это делает админка «Видео». */
import '../examples/examples.css';
import { state } from '../shared/state';
import { $, showToast } from '../shared/index';
import {
  escapeHtml,
  estimateDuration,
  isRecentMap,
  isStarterMap,
  primarySphere,
  sphereBadgeClass,
  sphereLabel,
  stepCountLabel,
  type ExampleListItem,
  type ExampleStepView,
} from '../examples/examples-shared';

interface ExampleStepDraft {
  sort_order: number;
  title: string;
  description: string;
  category: string;
  element_category: string;
  target_scale: 'daily' | 'qualitative';
  target_stage_index: number;
  assistant_prompt_template: string;
}

interface ExampleRecord extends ExampleListItem {
  is_published: boolean;
  sort_order: number;
  steps?: ExampleStepDraft[];
}

let adminCategoriesCache: string[] = [];
let editingMapId: string | null = null;
let editorSteps: ExampleStepDraft[] = [];
let adminMapsCache: ExampleRecord[] = [];
let adminFiltersBound = false;

const CHEVRON_SVG = '<svg class="rm-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>';
const EDIT_SVG = '<svg class="w-4 h-4" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>';
const LIST_SVG = '<svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>';

async function fetchAdminCategories(): Promise<string[]> {
  const r = await fetch('/api/admin/categories', {
    headers: { Authorization: 'Bearer ' + state.authToken },
  });
  if (!r.ok) return [];
  const data = await r.json() as Record<string, unknown>;
  return Object.keys(data || {}).sort();
}

function categoryOptionsHtml(selected = ''): string {
  return adminCategoriesCache.map((name) =>
    `<option value="${escapeHtml(name)}"${name === selected ? ' selected' : ''}>${escapeHtml(name)}</option>`
  ).join('');
}

function elementCategoryOptions(selected = 'work'): string {
  const opts = [
    { v: 'work', l: 'Работа' },
    { v: 'relation', l: 'Отношения' },
    { v: 'project', l: 'Проекты' },
  ];
  return opts.map((o) =>
    `<option value="${o.v}"${o.v === selected ? ' selected' : ''}>${o.l}</option>`
  ).join('');
}

function renderEditorSteps(): void {
  const container = $('admin-example-steps-container');
  if (!container) return;
  container.textContent = '';

  if (editorSteps.length === 0) {
    container.innerHTML = '<div class="audit-log-empty">Добавьте этапы примера.</div>';
    return;
  }

  editorSteps.forEach((step, idx) => {
    const el = document.createElement('div');
    el.className = 'user-item journey-stage-row';
    el.style.flexDirection = 'column';
    el.style.alignItems = 'stretch';
    el.style.gap = '8px';
    el.innerHTML = `
      <div style="display:flex; gap:8px; align-items:center; width:100%;">
        <span style="font-weight:bold; min-width:24px; color:var(--accent);">${idx + 1}</span>
        <input type="text" class="setup-input step-title" style="flex:1;" value="${escapeHtml(step.title)}" placeholder="Название этапа">
        <button type="button" class="nav-btn btn-action-sm move-up-step" data-idx="${idx}">▲</button>
        <button type="button" class="nav-btn btn-action-sm move-down-step" data-idx="${idx}">▼</button>
        <button type="button" class="nav-btn btn-action-sm--danger delete-step" data-idx="${idx}">🗑️</button>
      </div>
      <textarea class="setup-input step-desc" rows="2" placeholder="Описание этапа">${escapeHtml(step.description)}</textarea>
      <div style="display:grid; grid-template-columns: 1fr 1fr; gap:8px;">
        <label class="setup-label">Категория ИИ
          <select class="setup-input step-category">${categoryOptionsHtml(step.category)}</select>
        </label>
        <label class="setup-label">Сфера истории
          <select class="setup-input step-element-category">${elementCategoryOptions(step.element_category)}</select>
        </label>
      </div>
      <div style="display:grid; grid-template-columns: 1fr 1fr; gap:8px;">
        <label class="setup-label">Шкала
          <select class="setup-input step-scale">
            <option value="daily"${step.target_scale === 'daily' ? ' selected' : ''}>Ежедневный цикл</option>
            <option value="qualitative"${step.target_scale === 'qualitative' ? ' selected' : ''}>Качественная</option>
          </select>
        </label>
        <label class="setup-label">Индекс этапа
          <input type="number" class="setup-input step-stage-index" min="0" max="6" value="${step.target_stage_index}">
        </label>
      </div>
      <label class="setup-label">Шаблон промпта для Помощника (опционально)
        <textarea class="setup-input step-prompt" rows="2" placeholder="Переменные: \${exampleTitle}, \${stepTitle}, \${stepDesc}, \${previousStepsContextText}, \${currentText}">${escapeHtml(step.assistant_prompt_template)}</textarea>
      </label>
    `;

    el.querySelector('.step-title')?.addEventListener('input', (e) => {
      editorSteps[idx].title = (e.target as HTMLInputElement).value;
    });
    el.querySelector('.step-desc')?.addEventListener('input', (e) => {
      editorSteps[idx].description = (e.target as HTMLTextAreaElement).value;
    });
    el.querySelector('.step-category')?.addEventListener('change', (e) => {
      editorSteps[idx].category = (e.target as HTMLSelectElement).value;
    });
    el.querySelector('.step-element-category')?.addEventListener('change', (e) => {
      editorSteps[idx].element_category = (e.target as HTMLSelectElement).value;
    });
    el.querySelector('.step-scale')?.addEventListener('change', (e) => {
      editorSteps[idx].target_scale = (e.target as HTMLSelectElement).value as 'daily' | 'qualitative';
    });
    el.querySelector('.step-stage-index')?.addEventListener('input', (e) => {
      editorSteps[idx].target_stage_index = parseInt((e.target as HTMLInputElement).value, 10) || 0;
    });
    el.querySelector('.step-prompt')?.addEventListener('input', (e) => {
      editorSteps[idx].assistant_prompt_template = (e.target as HTMLTextAreaElement).value;
    });

    el.querySelector('.move-up-step')?.addEventListener('click', () => {
      if (idx > 0) {
        [editorSteps[idx - 1], editorSteps[idx]] = [editorSteps[idx], editorSteps[idx - 1]];
        renderEditorSteps();
      }
    });
    el.querySelector('.move-down-step')?.addEventListener('click', () => {
      if (idx < editorSteps.length - 1) {
        [editorSteps[idx + 1], editorSteps[idx]] = [editorSteps[idx], editorSteps[idx + 1]];
        renderEditorSteps();
      }
    });
    el.querySelector('.delete-step')?.addEventListener('click', () => {
      editorSteps.splice(idx, 1);
      renderEditorSteps();
    });

    container.appendChild(el);
  });
}

function showEditor(show: boolean): void {
  $('admin-example-editor')?.classList.toggle('hidden', !show);
  $('admin-examples-panel')?.classList.toggle('hidden', show);
}

function resetEditor(): void {
  editingMapId = null;
  editorSteps = [];
  const titleEl = $<HTMLInputElement>('admin-example-title');
  const descEl = $<HTMLTextAreaElement>('admin-example-description');
  const pubEl = $<HTMLInputElement>('admin-example-published');
  const sortEl = $<HTMLInputElement>('admin-example-sort');
  if (titleEl) titleEl.value = '';
  if (descEl) descEl.value = '';
  if (pubEl) pubEl.checked = false;
  if (sortEl) sortEl.value = '0';
  renderEditorSteps();
}

function renderAdminStats(maps: ExampleRecord[]): void {
  const totalSteps = maps.reduce((sum, m) => sum + (m.steps?.length ?? m.step_count ?? 0), 0);
  const starters = maps.filter((m) => (m.steps?.length ?? m.step_count ?? 0) >= 4).length;
  const shortMaps = maps.filter((m) => (m.steps?.length ?? m.step_count ?? 0) === 2).length;
  const published = maps.filter((m) => m.is_published).length;

  const totalEl = $('admin-rm-stat-total');
  const stepsEl = $('admin-rm-stat-steps');
  const starterEl = $('admin-rm-stat-starter');
  const shortEl = $('admin-rm-stat-short');
  const summaryEl = $('admin-examples-summary');
  const publishedPill = $('admin-examples-published-pill');
  const publishedCount = $('admin-examples-published-count');

  if (totalEl) totalEl.textContent = String(maps.length);
  if (stepsEl) stepsEl.textContent = String(totalSteps);
  if (starterEl) starterEl.textContent = String(starters);
  if (shortEl) shortEl.textContent = String(shortMaps);
  if (summaryEl) {
    summaryEl.textContent = maps.length
      ? `${maps.length} примеров · ${totalSteps} этапов · ежедневный цикл`
      : 'Примеров пока нет';
  }
  if (publishedPill) publishedPill.classList.toggle('hidden', maps.length === 0);
  if (publishedCount) publishedCount.textContent = `${published} опубликовано`;
}

function renderStepsTable(steps: ExampleStepView[]): string {
  if (!steps.length) {
    return '<div class="audit-log-empty">Этапы не заданы.</div>';
  }
  const rows = steps.map((step, idx) => {
    const sphere = step.element_category || '';
    const hasPrompt = !!(step.assistant_prompt_template?.trim());
    return `
      <tr>
        <td class="rm-cell-muted">${idx + 1}</td>
        <td>${escapeHtml(step.title)}</td>
        <td class="rm-cell-truncate" title="${escapeHtml(step.description || '')}">${escapeHtml(step.description || '—')}</td>
        <td><span class="rm-stage-chip">${step.target_stage_index ?? 0}</span></td>
        <td><span class="rm-badge ${sphereBadgeClass(sphere)}">${escapeHtml(sphere || '—')}</span></td>
        <td><span class="${hasPrompt ? 'rm-prompt-yes' : 'rm-prompt-no'}">${hasPrompt ? 'есть' : '—'}</span></td>
      </tr>
    `;
  }).join('');

  return `
    <div class="rm-admin-steps-table-wrap">
      <table class="rm-admin-steps-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Название этапа</th>
            <th>Описание (кратко)</th>
            <th>stage</th>
            <th>Сфера</th>
            <th>Промпт</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `;
}

function formatMapForClipboard(map: ExampleRecord): string {
  const lines = [
    `# ${map.title}`,
    map.description || '',
    `Опубликована: ${map.is_published ? 'да' : 'нет'} · Порядок: ${map.sort_order ?? 0}`,
    '',
    '## Этапы',
  ];
  (map.steps || []).forEach((step, idx) => {
    lines.push(
      `${idx + 1}. ${step.title}`,
      step.description || '',
      `   stage: ${step.target_stage_index ?? 0} · сфера: ${step.element_category || '—'}`
    );
  });
  return lines.join('\n');
}

function renderAdminMapCard(map: ExampleRecord, index: number): HTMLElement {
  const steps = map.steps || [];
  const stepCount = steps.length || map.step_count || 0;
  const sphere = primarySphere(map);
  const starter = isStarterMap(map, index);
  const recent = isRecentMap(map);
  const openFirst = index === 0;

  const badges: string[] = [];
  if (starter) badges.push('<span class="rm-badge rm-badge--start">стартовый</span>');
  if (recent) badges.push('<span class="rm-badge rm-badge--new">новый</span>');
  badges.push(
    map.is_published
      ? '<span class="rm-badge rm-badge--published">опубликована</span>'
      : '<span class="rm-badge rm-badge--draft">черновик</span>'
  );

  const article = document.createElement('article');
  article.className = 'rm-admin-card';
  article.dataset.title = map.title.toLowerCase();
  article.dataset.sphere = sphere || '';
  article.dataset.steps = String(stepCount);

  article.innerHTML = `
    <details class="rm-admin-card-details"${openFirst ? ' open' : ''}>
      <summary>
        <div class="rm-admin-card-summary">
          <div class="rm-admin-card-num">${index + 1}</div>
          <div class="rm-admin-card-body">
            <div style="display:flex; flex-wrap:wrap; align-items:center; gap:8px; margin-bottom:4px;">
              <h4 class="rm-admin-card-title">${escapeHtml(map.title)}</h4>
              ${badges.join('')}
            </div>
            <p class="rm-admin-card-desc">${escapeHtml(map.description || 'Без описания')}</p>
            <div class="rm-admin-card-meta">
              <span class="inline-flex" style="display:inline-flex;align-items:center;gap:4px;">${LIST_SVG} ${stepCountLabel(stepCount)}</span>
              <span>Порядок: <strong>${map.sort_order ?? 0}</strong></span>
              ${sphere ? `<span class="rm-badge ${sphereBadgeClass(sphere)}">${escapeHtml(sphere)}</span>` : ''}
            </div>
          </div>
          <div class="rm-admin-card-actions-top">
            <button type="button" class="rm-admin-icon-btn btn-edit-map" data-id="${map.id}" title="Редактировать" aria-label="Редактировать">${EDIT_SVG}</button>
            ${CHEVRON_SVG}
          </div>
        </div>
      </summary>
      <div class="rm-admin-card-expanded">
        <div class="rm-admin-steps-title">Этапы примера</div>
        ${renderStepsTable(steps)}
        <div class="rm-admin-card-footer">
          <button type="button" class="rm-admin-footer-btn rm-admin-footer-btn--ghost btn-copy-map" data-id="${map.id}">Копировать в буфер</button>
          <button type="button" class="rm-admin-footer-btn rm-admin-footer-btn--primary btn-edit-map-footer" data-id="${map.id}">Редактировать пример</button>
        </div>
      </div>
    </details>
  `;

  article.querySelectorAll('.btn-edit-map, .btn-edit-map-footer').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      void openEditor(map.id);
    });
  });

  article.querySelector('.btn-copy-map')?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = formatMapForClipboard(map);
    void navigator.clipboard.writeText(text).then(
      () => showToast('✅ Скопировано в буфер'),
      () => showToast('❌ Не удалось скопировать')
    );
  });

  return article;
}

function applyAdminFilters(): void {
  const q = ($<HTMLInputElement>('admin-examples-search')?.value || '').toLowerCase().trim();
  const sphere = $<HTMLSelectElement>('admin-examples-filter-sphere')?.value || '';
  const stepsFilter = $<HTMLSelectElement>('admin-examples-filter-steps')?.value || '';
  const list = $('admin-examples-list');
  if (!list) return;

  list.querySelectorAll<HTMLElement>('.rm-admin-card').forEach((card) => {
    const title = card.dataset.title || '';
    const cardSphere = card.dataset.sphere || '';
    const cardSteps = card.dataset.steps || '';
    const text = card.innerText.toLowerCase();
    const matchQ = !q || title.includes(q) || text.includes(q);
    const matchSphere = !sphere || cardSphere === sphere;
    const matchSteps = !stepsFilter || cardSteps === stepsFilter;
    card.classList.toggle('hidden-by-filter', !(matchQ && matchSphere && matchSteps));
  });
}

function renderAdminList(maps: ExampleRecord[]): void {
  const list = $('admin-examples-list');
  if (!list) return;
  list.textContent = '';

  if (!maps.length) {
    list.innerHTML = '<div class="audit-log-empty">Примеров пока нет.</div>';
    renderAdminStats([]);
    return;
  }

  const sorted = [...maps].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  sorted.forEach((map, index) => {
    list.appendChild(renderAdminMapCard(map, index));
  });

  renderAdminStats(sorted);
  applyAdminFilters();
}

function bindAdminFiltersOnce(): void {
  if (adminFiltersBound) return;
  adminFiltersBound = true;

  $('admin-examples-search')?.addEventListener('input', () => applyAdminFilters());
  $('admin-examples-filter-sphere')?.addEventListener('change', () => applyAdminFilters());
  $('admin-examples-filter-steps')?.addEventListener('change', () => applyAdminFilters());

  $('btn-admin-examples-expand')?.addEventListener('click', () => {
    $('admin-examples-list')?.querySelectorAll('details').forEach((d) => { d.open = true; });
  });
  $('btn-admin-examples-collapse')?.addEventListener('click', () => {
    $('admin-examples-list')?.querySelectorAll('details').forEach((d) => { d.open = false; });
  });
}

export async function loadAdminExamples(): Promise<void> {
  adminCategoriesCache = await fetchAdminCategories();
  bindAdminFiltersOnce();
  const list = $('admin-examples-list');
  if (!list) return;

  try {
    const r = await fetch('/api/admin/examples', {
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (!r.ok) throw new Error('Не удалось загрузить примеры');
    adminMapsCache = await r.json() as ExampleRecord[];
    renderAdminList(adminMapsCache);
  } catch (err) {
    console.error(err);
    list.innerHTML = '<div class="audit-log-empty">Не удалось загрузить примеры.</div>';
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  }
}

async function openEditor(id: string | null): Promise<void> {
  adminCategoriesCache = await fetchAdminCategories();
  resetEditor();
  editingMapId = id;

  if (id) {
    const cached = adminMapsCache.find((m) => m.id === id);
    let map = cached;
    if (!map?.steps?.length) {
      const r = await fetch(`/api/admin/examples/${encodeURIComponent(id)}`, {
        headers: { Authorization: 'Bearer ' + state.authToken },
      });
      if (!r.ok) {
        showToast('❌ Не удалось загрузить пример');
        return;
      }
      map = await r.json() as ExampleRecord;
    }
    if (!map) {
      showToast('❌ Карта не найдена');
      return;
    }
    $<HTMLInputElement>('admin-example-title')!.value = map.title;
    $<HTMLTextAreaElement>('admin-example-description')!.value = map.description || '';
    $<HTMLInputElement>('admin-example-published')!.checked = map.is_published;
    $<HTMLInputElement>('admin-example-sort')!.value = String(map.sort_order ?? 0);
    editorSteps = (map.steps || []).map((s, i) => ({
      sort_order: s.sort_order ?? i,
      title: s.title || '',
      description: s.description || '',
      category: (s as ExampleStepDraft).category || adminCategoriesCache[0] || '',
      element_category: s.element_category || 'work',
      target_scale: (s as ExampleStepDraft).target_scale || 'daily',
      target_stage_index: s.target_stage_index ?? 0,
      assistant_prompt_template: s.assistant_prompt_template || '',
    }));
    const titleHeader = $('admin-example-editor-title');
    if (titleHeader) titleHeader.textContent = 'Редактор примера';
  } else {
    editorSteps = [{
      sort_order: 0,
      title: '',
      description: '',
      category: adminCategoriesCache[0] || '',
      element_category: 'work',
      target_scale: 'daily',
      target_stage_index: 0,
      assistant_prompt_template: '',
    }];
    const titleHeader = $('admin-example-editor-title');
    if (titleHeader) titleHeader.textContent = 'Новый пример';
  }

  renderEditorSteps();
  showEditor(true);
}

async function saveEditor(): Promise<void> {
  const title = $<HTMLInputElement>('admin-example-title')?.value.trim() || '';
  if (!title) {
    showToast('❌ Укажите название примера');
    return;
  }

  for (let i = 0; i < editorSteps.length; i++) {
    if (!editorSteps[i].title.trim()) {
      showToast(`❌ Название этапа ${i + 1} обязательно`);
      return;
    }
    if (!editorSteps[i].category) {
      showToast(`❌ Категория ИИ для этапа ${i + 1} обязательна`);
      return;
    }
  }

  const payload = {
    title,
    description: $<HTMLTextAreaElement>('admin-example-description')?.value.trim() || null,
    is_published: $<HTMLInputElement>('admin-example-published')?.checked === true,
    sort_order: parseInt($<HTMLInputElement>('admin-example-sort')?.value || '0', 10) || 0,
    steps: editorSteps.map((s, i) => ({
      sort_order: i,
      title: s.title.trim(),
      description: s.description.trim() || null,
      category: s.category,
      element_category: s.element_category || 'work',
      target_scale: s.target_scale,
      target_stage_index: s.target_stage_index,
      assistant_prompt_template: s.assistant_prompt_template.trim() || null,
    })),
  };

  const url = editingMapId
    ? `/api/admin/examples/${encodeURIComponent(editingMapId)}`
    : '/api/admin/examples';
  const method = editingMapId ? 'PUT' : 'POST';

  const r = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + state.authToken!,
    },
    body: JSON.stringify(payload),
  });

  if (r.ok) {
    showToast('✅ Пример сохранена');
    showEditor(false);
    await loadAdminExamples();
  } else {
    const err = await r.json().catch(() => ({})) as { detail?: string; error?: { message?: string } };
    showToast('❌ ' + (err.detail || err.error?.message || 'Ошибка сохранения'));
  }
}

async function deleteEditor(): Promise<void> {
  if (!editingMapId) return;
  if (!confirm('Удалить эту пример?')) return;

  const r = await fetch(`/api/admin/examples/${encodeURIComponent(editingMapId)}`, {
    method: 'DELETE',
    headers: { Authorization: 'Bearer ' + state.authToken! },
  });

  if (r.ok) {
    showToast('Карта удалена');
    showEditor(false);
    await loadAdminExamples();
  } else {
    showToast('❌ Ошибка удаления');
  }
}

export function bindAdminExamplesEvents(): void {
  $('btn-admin-example-new')?.addEventListener('click', () => void openEditor(null));
  $('btn-admin-example-cancel')?.addEventListener('click', () => showEditor(false));
  $('btn-admin-example-add-step')?.addEventListener('click', () => {
    editorSteps.push({
      sort_order: editorSteps.length,
      title: '',
      description: '',
      category: adminCategoriesCache[0] || '',
      element_category: 'work',
      target_scale: 'daily',
      target_stage_index: 0,
      assistant_prompt_template: '',
    });
    renderEditorSteps();
  });
  $('btn-admin-example-save')?.addEventListener('click', () => void saveEditor());
  $('btn-admin-example-delete')?.addEventListener('click', () => void deleteEditor());
}
